import { withRouter as foundWithRouter, type RouterState } from 'found';
import type React from 'react';

import { useResolvedMatch } from './useResolvedMatch';

/**
 * found's own `withRouter` with the resolved-match filter applied to the
 * `match` it injects. found 1.3 publishes the pending match on its router
 * context, so this — never found's `withRouter` — is the HOC a component uses.
 * Under the suite-wide `found` mock it reduces to the filter alone, which
 * passes an unresolved or absent `match` through.
 */
const withRouter = <P extends RouterState>(
	Component: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof RouterState>> => {
	const name = Component.displayName || Component.name;

	const Resolved = (props: P) => {
		const match = useResolvedMatch(props.match);
		return <Component {...props} match={match} />;
	};
	Resolved.displayName = `ResolvedMatch(${name})`;

	// found names its result after the component it receives, which would be
	// the wrapper above.
	const WithRouter = foundWithRouter(Resolved);
	WithRouter.displayName = `withRouter(${name})`;
	return WithRouter;
};

export default withRouter;
